import { ADULTS_KEY, STAGE_KEYS, type AudienceKey } from '@yoyoclub/shared';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { focusRing } from '../ui/redesign';

import { AUDIENCE_THEME } from './homeState';

const OPTIONS: AudienceKey[] = [...STAGE_KEYS, ADULTS_KEY];

/**
 * The age "ruler" (prototype `.ruler` / `.seg`): five stages and "给家长", one style,
 * each with its own color bar. Six in a row on desktop, 3 x 2 on phones and in the
 * phone stage sheet (`compact`).
 */
export default function StageSelector({
  value,
  onChange,
  compact = false,
}: {
  value: AudienceKey;
  onChange: (next: AudienceKey) => void;
  compact?: boolean;
}) {
  const { t } = useTranslation('home');
  return (
    <div
      role="group"
      aria-label={t('selector.label')}
      className={`grid gap-2 ${compact ? 'grid-cols-3' : 'grid-cols-3 desk:grid-cols-6'}`}
    >
      {OPTIONS.map((key) => {
        const theme = AUDIENCE_THEME[key];
        const isParents = key === ADULTS_KEY;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={value === key}
            onClick={() => onChange(key)}
            style={{ '--sc': theme.c, '--son': theme.on } as CSSProperties}
            className={`group relative rounded-2xl border-[1.5px] border-line bg-white px-1.5 pb-[9px] pt-2.5 text-center font-ui transition-[transform,background-color,border-color] duration-150 after:absolute after:-bottom-[1.5px] after:left-3 after:right-3 after:h-1 after:rounded-t after:bg-[var(--sc)] after:opacity-85 after:content-[''] hover:-translate-y-0.5 hover:border-[var(--sc)] aria-pressed:border-[var(--sc)] aria-pressed:bg-[var(--sc)] aria-pressed:after:opacity-0 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${focusRing}`}
          >
            {isParents ? (
              <span className="block text-[15px] font-bold leading-[21.6px] text-ink group-aria-pressed:text-[var(--son)]">
                {t('selector.parents')}
              </span>
            ) : (
              <span className="block text-[18px] font-bold leading-[1.2] text-ink group-aria-pressed:text-[var(--son)]">
                {theme.age}
                <small className="ml-px text-[12px] font-semibold">{t('selector.yearsUnit')}</small>
              </span>
            )}
            <span className="mt-0.5 block font-text text-[12px] leading-[1.6] text-mute [text-wrap:balance] group-aria-pressed:text-[var(--son)]">
              {isParents ? t('selector.parentsSub') : t(`stages.${key}.name`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
